import { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { COMMITMENT_TYPE_OPTIONS, COMPENSATION_TYPE_OPTIONS } from '@libs/types';
import { PostStepperDto } from '@libs/dto';
import {
  TextInput,
  Select,
  CheckBoxInput,
  FreeSoloInput,
  RichTextEditor,
  CheckboxListInput,
  JobPostSelect,
  InfoButton,
  JobTemplateHelpContent,
} from '@carcareers/components';
import { useForm } from '@carcareers/contexts';
import { JOB_POSITIONS, BENEFITS } from '@carcareers/utils';

interface JobStepFormProps {
  rooftopName?: string;
}

export const JobStepForm: FC<JobStepFormProps> = ({ rooftopName = 'rooftop' }) => {
  const { data, errors, getErrors, setData } = useForm<PostStepperDto>();
  const [descriptionError, setDescriptionError] = useState<string>();
  const [jobError, setJobError] = useState<string>();

  const disabled = !!data?.job?.id;

  useEffect(() => {
    if (errors) {
      setDescriptionError(getErrors('job.description'));
      setJobError(getErrors('job'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
        <Typography variant="h2">Set up your Job Template</Typography>
        <InfoButton tooltip="What are job templates?" title="About Job Templates">
          <JobTemplateHelpContent />
        </InfoButton>
      </Box>

      <Typography variant="body1">
        Choose an existing job template from your selected rooftop, or create a new one. Creating a new rooftop?
        You&apos;ll need to create a new job template as well.
      </Typography>

      {jobError && !data?.rooftop?.id && (
        <Typography variant="subtitle2" sx={{ color: 'error.main' }}>
          {jobError}
        </Typography>
      )}

      {data?.rooftop?.id && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4">Select an Existing Job Template</Typography>
          </Box>
          <JobPostSelect name="job" />
        </>
      )}
      {!disabled && (
        <Typography variant="h4">{data?.rooftop?.id ? 'Or Create a New Job' : 'Create a New Job'}</Typography>
      )}
      <TextInput label="Title" name="job.title" disabled={disabled} />
      <Select
        fullWidth
        label="Commitment Type"
        name="job.commitmentType"
        options={COMMITMENT_TYPE_OPTIONS}
        disabled={disabled}
      />
      <Typography variant="h6">Compensation</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextInput fullWidth label="($) Min" name="job.compensationMin" type="currency" disabled={disabled} />
        <TextInput fullWidth label="($) Max" name="job.compensationMax" type="currency" disabled={disabled} />
        <Select
          fullWidth
          label="Per"
          name="job.compensationType"
          options={COMPENSATION_TYPE_OPTIONS}
          disabled={disabled}
        />
      </Box>
      <CheckBoxInput label="Commission" name="job.hasCommission" disabled={disabled} />
      <Typography variant="h6">Keywords</Typography>
      <FreeSoloInput
        multiple
        disableCloseOnSelect
        label="Keywords"
        name="job.keywords"
        suggestions={JOB_POSITIONS}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.position)}
        getOptionValue={(option) => (typeof option === 'string' ? option : option.position)}
        groupBy={(option) => (typeof option === 'string' ? 'Positions' : option.department)}
        value={data?.job?.keywords}
        disabled={disabled}
      />
      <Typography variant="h6">Benefits</Typography>
      <CheckboxListInput
        name="job.benefits"
        variant="chip"
        options={BENEFITS.map((benefit) => ({ label: benefit.name, value: benefit.value }))}
        disabled={disabled}
      />
      <Typography variant="h6">Description</Typography>
      {descriptionError && (
        <Typography variant="subtitle2" sx={{ color: 'error.main' }}>
          {descriptionError}
        </Typography>
      )}
      <RichTextEditor
        data={data?.job?.description}
        onUpdate={(value) => setData('job.description', value)}
        readOnly={disabled}
        fillHeight={disabled}
      />
    </>
  );
};

export default JobStepForm;
